import { useRef, useState, useLayoutEffect } from 'react';
import { Box, Image, Badge } from '@mantine/core';

interface HaiveLogoProps {
  /** Logo height in px (default 30) */
  h?: number;
  /** Show the ALPHA badge (default true) */
  showAlpha?: boolean;
  /** Visual variant */
  variant?: 'default' | 'dimmed';
  /** Click handler (adds pointer cursor) */
  onClick?: () => void;
}

/**
 * SVG viewBox is 681×289.
 * "HAIVE" text paths span y 143.66 → 233.33.
 * Text vertical-centre as a fraction of SVG height:
 *   (143.66 + 233.33) / 2 / 289 ≈ 0.652
 */
const TEXT_CENTER_FRAC = (143.66 + 233.33) / 2 / 289; // ≈ 0.6522

/** Aspect ratio from SVG viewBox (681 / 289) */
const LOGO_ASPECT = 681 / 289;

export function HaiveLogo({
  h = 30,
  showAlpha = true,
  variant = 'default',
  onClick,
}: HaiveLogoProps) {
  const dimmed = variant === 'dimmed';
  const badgeRef = useRef<HTMLDivElement>(null);
  const [badgeTop, setBadgeTop] = useState<number>(h * TEXT_CENTER_FRAC - 8);

  // After paint, measure real badge height and position its centre
  // on the HAIVE text centre. +1px compensates for caps sitting
  // slightly above the badge-box mid-line.
  useLayoutEffect(() => {
    if (!showAlpha || !badgeRef.current) return;
    const badgeH = badgeRef.current.getBoundingClientRect().height;
    const textCenter = h * TEXT_CENTER_FRAC;
    setBadgeTop(textCenter - badgeH / 2 + 1);
  }, [h, showAlpha]);

  return (
    <Box
      style={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: 8,
        cursor: onClick ? 'pointer' : undefined,
      }}
      onClick={onClick}
    >
      <Image
        src="/logo.svg"
        alt="Haive"
        h={h}
        w={Math.round(h * LOGO_ASPECT)}
        fit="contain"
        style={{ opacity: dimmed ? 0.4 : 1, flexShrink: 0 }}
      />
      {showAlpha && (
        <Badge
          ref={badgeRef}
          variant="filled"
          size="xs"
          color={dimmed ? 'gray' : 'haiveOrange'}
          radius="sm"
          style={{
            fontWeight: 700,
            letterSpacing: '.06em',
            fontSize: h < 24 ? 7 : 9,
            opacity: dimmed ? 0.5 : 1,
            marginTop: badgeTop,
            flexShrink: 0,
          }}
        >
          ALPHA
        </Badge>
      )}
    </Box>
  );
}
